import { useState, useEffect, useCallback } from 'react';
import type { BlogPost } from '@/mocks/blog';
import { initialBlogPosts } from '@/mocks/blog';

const STORAGE_KEY = 'ak_energy_blog_posts';
const PREVIEW_KEY = 'ak_energy_blog_preview';

function loadPosts(): BlogPost[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as BlogPost[];
      return parsed.length > 0 ? parsed : initialBlogPosts;
    }
  } catch {
    // ignore
  }
  return initialBlogPosts;
}

function savePosts(posts: BlogPost[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
  } catch {
    // ignore storage errors
  }
}

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>(loadPosts);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    savePosts(posts);
  }, [posts]);

  const getPostBySlug = useCallback(
    (slug: string): BlogPost | undefined => {
      return posts.find((p) => p.slug === slug);
    },
    [posts]
  );

  const getPublishedPosts = useCallback((): BlogPost[] => {
    return posts
      .filter((p) => p.status === 'published')
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }, [posts]);

  const createPost = useCallback((post: Omit<BlogPost, 'id' | 'createdAt'>): BlogPost => {
    const newPost: BlogPost = {
      ...post,
      id: crypto.randomUUID(),
      publishedAt: post.status === 'published' ? new Date().toISOString().split('T')[0] : post.publishedAt,
      updatedAt: new Date().toISOString().split('T')[0],
    };
    setPosts((prev) => [newPost, ...prev]);
    return newPost;
  }, []);

  const updatePost = useCallback((id: string, updates: Partial<BlogPost>): BlogPost | null => {
    let updated: BlogPost | null = null;
    setPosts((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) return prev;
      updated = { ...prev[idx], ...updates, updatedAt: new Date().toISOString().split('T')[0] };
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
    return updated;
  }, []);

  const deletePost = useCallback((id: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const savePreview = useCallback((post: BlogPost) => {
    try {
      localStorage.setItem(PREVIEW_KEY, JSON.stringify(post));
    } catch {
      // ignore
    }
  }, []);

  const getPreview = useCallback((): BlogPost | null => {
    try {
      const raw = localStorage.getItem(PREVIEW_KEY);
      if (raw) return JSON.parse(raw) as BlogPost;
    } catch {
      // ignore
    }
    return null;
  }, []);

  const clearPreview = useCallback(() => {
    localStorage.removeItem(PREVIEW_KEY);
  }, []);

  return {
    posts,
    loading,
    getPostBySlug,
    getPublishedPosts,
    createPost,
    updatePost,
    deletePost,
    savePreview,
    getPreview,
    clearPreview,
  };
}